import { bytesToBase64, base64ToBytes } from '../crypto/base64.ts'
import { getBaseNickname } from '../identity/ZionIdentityManager.ts'
import { getPublicKeyBase64 } from '../storage/keyStore.ts'
import { getInboundQueueId, getSessionToken } from '../storage/sessionStore.ts'

export const PAIRING_PAYLOAD_PREFIX = 'ZION1::'
export const PAIRING_PAYLOAD_TTL_MS = 10 * 60 * 1000

export interface PairingPayloadData {
  sessionToken: string
  publicKey: string
  nickname: string
  queueId: string
  exp: number
}

function base64UrlEncodeUtf8(text: string): string {
  return bytesToBase64(new TextEncoder().encode(text))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

function base64UrlDecodeUtf8(encoded: string): string {
  const pad = '='.repeat((4 - (encoded.length % 4)) % 4)
  const b64 = (encoded + pad).replace(/-/g, '+').replace(/_/g, '/')
  return new TextDecoder().decode(base64ToBytes(b64))
}

export async function buildPairingPayload(): Promise<{ payload: string; exp: number }> {
  try {
    const session = (await getSessionToken())?.trim()
    const publicKey = (await getPublicKeyBase64()).trim()
    const nickname = await getBaseNickname()
    const queueId = (await getInboundQueueId())?.trim() ?? ''
    if (!session || !publicKey) throw new Error('Sessione non pronta')

    const exp = Date.now() + PAIRING_PAYLOAD_TTL_MS
    const json = JSON.stringify({
      v: 1,
      s: session,
      k: publicKey,
      n: nickname,
      q: queueId,
      exp,
    })
    const payload = `${PAIRING_PAYLOAD_PREFIX}${base64UrlEncodeUtf8(json)}`
    if (!payload.startsWith(PAIRING_PAYLOAD_PREFIX) || payload.length < 20) {
      throw new Error('pairing_payload_invalid')
    }
    return { payload, exp }
  } catch (err) {
    console.error('[ZION] buildPairingPayload failed', err)
    throw err instanceof Error ? err : new Error(String(err))
  }
}

export function parsePairingPayload(input: string): PairingPayloadData | null {
  try {
    const normalized = input.trim().replace(/\s+/g, '')
    if (!normalized.startsWith(PAIRING_PAYLOAD_PREFIX)) return null
    const encoded = normalized.slice(PAIRING_PAYLOAD_PREFIX.length)
    const json = JSON.parse(base64UrlDecodeUtf8(encoded)) as {
      v?: number
      s?: string
      k?: string
      n?: string
      q?: string
      exp?: number
    }
    if (json.v !== 1) return null
    const exp = Number(json.exp ?? 0)
    if (!exp || Date.now() > exp) return null
    const sessionToken = String(json.s ?? '').trim()
    const publicKey = String(json.k ?? '').trim()
    const nickname = String(json.n ?? '').trim()
    const queueId = String(json.q ?? '').trim()
    if (!sessionToken || !publicKey) return null
    return { sessionToken, publicKey, nickname, queueId, exp }
  } catch {
    return null
  }
}

export function pairingCountdownText(exp: number): string {
  const sec = Math.max(0, Math.floor((exp - Date.now()) / 1000))
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${m}:${s.toString().padStart(2, '0')}`
}
